import { combineLatest, fromEvent, Observable } from 'rxjs';
import { debounceTime, map, shareReplay, startWith } from 'rxjs/operators';

export interface DirtyComponent {
  isDirty$: Observable<boolean>;
  openConfirmation?: () => Observable<boolean>;
}
// Normalize for deep comparison
const normalize = (val: any): any => {
  if (val == null) return '';
  if (typeof val === 'string' && !isNaN(Number(val))) return Number(val);
  if (Array.isArray(val)) return val.map(normalize);
  if (typeof val === 'object') {
    const result: Record<string, any> = {};
    for (const [key, value] of Object.entries(val)) {
      result[key] = normalize(value);
    }
    return result;
  }
  return val;
};

const isEqual = (a: any, b: any, visited = new WeakMap()): boolean => {
  if (a === b) return true;
  if (typeof a !== typeof b || a == null || b == null) return false;
  if (typeof a !== 'object') return a === b;
  if (visited.has(a)) return visited.get(a) === b;
  visited.set(a, b);
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => isEqual(a[k], b[k], visited));
};

const getDiff = (a: any = {}, b: any = {}, visited = new WeakMap()): any => {
  if (a === b) return {};
  visited.set(a, b);
  const diff: any = {};
  for (const [k, newVal] of Object.entries(b)) {
    const oldVal = a[k];
    if (!isEqual(oldVal, newVal, visited)) {
      if (Array.isArray(newVal) && Array.isArray(oldVal)) {
        const added = newVal.filter((x) => !oldVal.includes(x));
        const removed = oldVal.filter((x) => !newVal.includes(x));
        if (added.length || removed.length) diff[k] = { added, removed };
      } else if (typeof newVal === 'object' && typeof oldVal === 'object') {
        const nested = getDiff(oldVal, newVal, visited);
        if (Object.keys(nested).length) diff[k] = nested;
      } else {
        diff[k] = newVal;
      }
    }
  }
  return diff;
};

export function dirtyCheck<U>(source: Observable<U>) {
  const unload$ = fromEvent<BeforeUnloadEvent>(window, 'beforeunload');
  return <T>(valueChanges: Observable<T>): Observable<boolean> => {
    const isDirty$ = combineLatest([source, valueChanges]).pipe(
      debounceTime(300),
      map(([a, b]) => Object.keys(getDiff(normalize(a), normalize(b))).length > 0),
      startWith(false),
      shareReplay(1)
    );
    unload$.subscribe((event) => {
      isDirty$.subscribe((isDirty) => {
        if (isDirty) {
          event.preventDefault();
          event.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        }
      });
    });
    return isDirty$;
  };
}

// Utility: Array asymmetric difference
export function asymmetricArrayDiff<T extends String | Number | Boolean>(arr1: T[], arr2: T[]): T[] {
  arr1 = arr1 || [];
  arr2 = arr2 || [];

  const diff = arr1.filter((x) => !arr2.includes(x)).concat(arr2.filter((x) => !arr1.includes(x)));
  return diff;
}
